import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountIdLoggedInCanActivate } from '../../guards';
import { AppInsufficientPermissionRouteParams } from '../../core/routeparams';
import { HttpClient } from '@angular/common/http';
import { IAMService } from '@vendasta/iamv2';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom, timeout } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UserService } from '../../core/user.service';

export type RequestAccessState = 'idle' | 'submitting' | 'submitted' | 'error';

@Component({
  selector: 'app-insufficient-permission',
  templateUrl: './app-insufficient-permission.component.html',
  styleUrls: ['./app-insufficient-permission.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppInsufficientPermissionComponent implements OnInit {
  accountGroupId: string;
  signinLink: string;
  entitlementId: string | null = null;
  partnerId: string | null = null;
  requestState: RequestAccessState = 'idle';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private iamService: IAMService,
    private userService: UserService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.accountGroupId = (this.route.snapshot.params as AppInsufficientPermissionRouteParams).accountGroupId;

    if (this.accountGroupId) {
      this.signinLink = AccountIdLoggedInCanActivate.buildLoginUrl(
        this.accountGroupId,
        `/entry/${this.accountGroupId}`,
      );
    }

    const queryParams = this.route.snapshot.queryParams;
    this.entitlementId = queryParams['entitlementId'] || null;
    this.partnerId = queryParams['partnerId'] || null;
  }

  get isEntitlementRequestable(): boolean {
    return !!this.entitlementId && !!this.partnerId;
  }

  async requestAccess(): Promise<void> {
    if (!this.isEntitlementRequestable || this.requestState === 'submitting') {
      return;
    }
    this.requestState = 'submitting';
    try {
      const persona = await firstValueFrom(
        this.userService.persona$.pipe(
          filter((p) => !!p),
          take(1),
          timeout(10000),
        ),
      );
      await firstValueFrom(
        this.iamService.createPamGrant(
          persona.userId,
          this.entitlementId,
          'Requesting access to a foundational client account (Website Pro)',
          this.partnerId,
          this.accountGroupId,
        ),
      );
      this.requestState = 'submitted';
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        this.requestState = 'submitted';
        return;
      }
      this.requestState = 'error';
      this.snackbarService.openErrorSnack('APP_INSUFFICIENT_PERMISSION.REQUEST_ACCESS_ERROR');
    }
  }
}
